import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { CoffeeViewNavState, SearchResultsNavState } from '@/analytics/events';
import { newSearchId, trackCoffeeSearched, trackSearchResultClicked } from '@/analytics/events';
import { MIN_QUERY_LENGTH } from '@/api/queries/search';
import { Layout } from '@/components/Layout';
import { CantFindItCTA } from '@/components/search/CantFindItCTA';
import { SearchResultRow } from '@/components/search/SearchResultRow';
import { useSearchResults } from '@/hooks/useSearchResults';

// The `search_id` a dropdown "view all" carried here, but only when it is for
// the query we're actually showing — so a stale history entry can't attach an
// old id to a different query.
function reusableSearchId(state: unknown, query: string): string | undefined {
  if (state !== null && typeof state === 'object' && 'search_id' in state && 'query' in state) {
    const { search_id, query: stateQuery } = state as SearchResultsNavState;
    if (typeof search_id === 'string' && stateQuery === query) {
      return search_id;
    }
  }
  return undefined;
}

export function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const navState: unknown = location.state;
  const query = (searchParams.get('q') ?? '').trim();
  const reuseId = reusableSearchId(navState, query);

  // One `search_id` per settled query (#448), minted on its first page. Kept
  // in state so the result links and request CTA render with it; the ref keeps
  // it stable across re-renders (and "Load more") until the query changes.
  const [searchId, setSearchId] = useState<string | null>(null);
  const searchIdForQuery = useRef<{ query: string; id: string } | null>(null);

  // `coffee_searched` fires once per query: the hook invokes `onFirstPage`
  // only for the initial page of the *current* query — never for "Load more"
  // appends, aborted requests, or superseded-query completions.
  const { results, isLoading, error, loadMore, hasMore } = useSearchResults(query, {
    onFirstPage: (searchedQuery, resultCount) => {
      // Reuse the view-all id when this query continues that dropdown attempt,
      // otherwise mint a fresh one (refine, cleared/restarted, direct URL).
      const id =
        searchIdForQuery.current?.query === searchedQuery
          ? searchIdForQuery.current.id
          : (reuseId ?? newSearchId());
      searchIdForQuery.current = { query: searchedQuery, id };
      setSearchId(id);
      trackCoffeeSearched({
        query: searchedQuery,
        result_count: resultCount,
        surface: 'results_page',
        search_id: id,
      });
      // Consume the view-all state: React Router restores `location.state` from
      // `window.history.state` on a full reload and browser-back, which would
      // otherwise re-fire `coffee_searched` under the *same* reused id on every
      // revisit. Clearing it (as `CoffeeDetailPage` does for source) makes a
      // reload/back count as a new attempt — a fresh id — matching a direct URL.
      if (navState !== null) {
        void navigate(`${pathname}${search}`, { replace: true, state: null });
      }
    },
  });

  const isQueryTooShort = query.length < MIN_QUERY_LENGTH;

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-6">
        <h1 className="mb-5 text-2xl font-bold text-black">
          {query ? `Results for “${query}”` : 'Search coffees'}
        </h1>

        {isQueryTooShort && (
          <p className="text-black/60">Type at least {MIN_QUERY_LENGTH} characters to search.</p>
        )}

        {!isQueryTooShort && error && (
          <p className="text-wolisho-red">Search failed. Please try again.</p>
        )}

        {!isQueryTooShort && !error && results.length === 0 && !isLoading && (
          <div>
            <p className="text-center text-black/60">No matches for “{query}”.</p>
            <CantFindItCTA query={query} variant="page-empty" searchId={searchId ?? newSearchId()} />
          </div>
        )}

        {results.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {results.map((result, index) => {
              const sid = searchId ?? newSearchId();
              return (
              <li key={result.id}>
                <Link
                  to={`/coffee/${result.id}`}
                  state={
                    { source: 'search_results', search_id: sid } satisfies CoffeeViewNavState
                  }
                  className="block"
                  onClick={() =>
                    trackSearchResultClicked({
                      query,
                      surface: 'results_page',
                      target: 'coffee',
                      position: index,
                      search_id: sid,
                    })
                  }
                >
                  <SearchResultRow result={result} variant="card" />
                </Link>
              </li>
              );
            })}
          </ul>
        )}

        {isLoading && (
          <p className="mt-4 text-center text-sm text-black/60">Loading…</p>
        )}

        {!isQueryTooShort && results.length > 0 && (
          <div className="mt-5 flex flex-col items-center gap-3.5">
            {hasMore && !isLoading && (
              <button
                type="button"
                onClick={() => void loadMore()}
                className="rounded-lg border-2 border-wolisho-brown bg-transparent px-6 py-3 text-sm font-bold text-wolisho-brown transition-colors hover:bg-wolisho-brown hover:text-white"
              >
                Load more
              </button>
            )}
            <CantFindItCTA query={query} variant="page-footer" searchId={searchId ?? newSearchId()} />
          </div>
        )}
      </div>
    </Layout>
  );
}
